import React, { useState } from "react";
import { api, unwrap, errMsg } from "@/lib/api";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { formatMoney, formatDate } from "@/lib/utils";
import { Plus, BookOpen, Search, ArrowRight } from "lucide-react";
import { useI18n } from "@/i18n";
import { useLookupValues } from "@/lib/platformConfig";

export default function LibraryPage() {
  const { t } = useI18n();
  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="library-page">
      <div className="mb-6">
        <h1 className="font-display text-3xl font-bold text-[var(--text-primary)]">{t("management.libraryTitle")}</h1>
        <p className="text-sm text-[var(--text-muted)] mt-1">{t("management.librarySubtitle")}</p>
      </div>
      <Tabs defaultValue="books">
        <TabsList>
          <TabsTrigger value="books" data-testid="tab-books">Books</TabsTrigger>
          <TabsTrigger value="issues" data-testid="tab-issues">Issues</TabsTrigger>
        </TabsList>
        <TabsContent value="books"><BooksTab /></TabsContent>
        <TabsContent value="issues"><IssuesTab /></TabsContent>
      </Tabs>
    </div>
  );
}

function BooksTab() {
  const [q, setQ] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [issueOpen, setIssueOpen] = useState(null);
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["books", q],
    queryFn: async () => unwrap(await api.get("/library/books", { params: q ? { q } : {} })),
  });
  const qc = useQueryClient();
  const createMut = useMutation({
    mutationFn: async (form) => unwrap(await api.post("/library/books", form)),
    onSuccess: () => { toast.success("Book added"); refetch(); setAddOpen(false); },
    onError: (e) => toast.error(errMsg(e)),
  });

  return (
    <div className="mt-6 space-y-4">
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="h-4 w-4 text-[var(--text-muted)] absolute left-3 top-1/2 -translate-y-1/2" />
          <Input className="pl-9" placeholder="Search by title, author, ISBN…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="book-search" />
        </div>
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white" data-testid="new-book-btn">
              <Plus className="h-4 w-4 mr-1.5" /> Add book
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-display">Add book</DialogTitle></DialogHeader>
            <BookForm onSubmit={(f) => createMut.mutate(f)} loading={createMut.isPending} />
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border-[var(--subtle)]">
        <CardContent className="p-0">
          {isLoading ? <Skeleton className="h-64" /> : (
          <div className="overflow-x-auto"><table className="w-full text-sm grid-table">
              <thead><tr>
                <th className="text-left px-3 py-3">Title</th>
                <th className="text-left px-3 py-3">Author</th>
                <th className="text-left px-3 py-3">Category</th>
                <th className="text-right px-3 py-3">Available</th>
                <th className="text-right px-3 py-3">Total</th>
                <th className="text-left px-3 py-3">Action</th>
              </tr></thead>
              <tbody>
                {(data || []).map((b) => (
                  <tr key={b.id} data-testid={`book-${b.id}`}>
                    <td className="px-3 py-3 font-medium">{b.title}</td>
                    <td className="px-3 py-3 text-[var(--text-secondary)]">{b.author || "—"}</td>
                    <td className="px-3 py-3 text-xs">{b.category || "—"}</td>
                    <td className="px-3 py-3 text-right font-mono-num font-semibold text-[var(--success)]">{b.copies_available}</td>
                    <td className="px-3 py-3 text-right font-mono-num">{b.copies_total}</td>
                    <td className="px-3 py-3">
                      <Button size="sm" onClick={() => setIssueOpen(b)} disabled={b.copies_available <= 0} className="h-8 bg-[var(--brand)] hover:bg-[var(--brand-hover)] text-white" data-testid={`issue-book-${b.id}`}>
                        Issue
                      </Button>
                    </td>
                  </tr>
                ))}
                {(data || []).length === 0 && !isLoading && <tr><td colSpan={6} className="py-8 text-center text-[var(--text-muted)]">No books.</td></tr>}
              </tbody>
          </table></div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!issueOpen} onOpenChange={(v) => !v && setIssueOpen(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">Issue book</DialogTitle></DialogHeader>
          {issueOpen && <IssueForm book={issueOpen} onSuccess={() => { setIssueOpen(null); qc.invalidateQueries({ queryKey: ["books"] }); qc.invalidateQueries({ queryKey: ["issues"] }); }} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function BookForm({ onSubmit, loading }) {
  const [form, setForm] = useState({ title: "", author: "", isbn: "", category: "", copies_total: 1 });
  return (
    <div className="space-y-3">
      <div><Label className="text-xs uppercase">Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1" data-testid="book-title" /></div>
      <div><Label className="text-xs uppercase">Author</Label><Input value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} className="mt-1" /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label className="text-xs uppercase">ISBN</Label><Input value={form.isbn} onChange={(e) => setForm({ ...form, isbn: e.target.value })} className="mt-1 font-mono-num" /></div>
        <div><Label className="text-xs uppercase">Copies</Label><Input type="number" min="1" value={form.copies_total} onChange={(e) => setForm({ ...form, copies_total: Number(e.target.value) })} className="mt-1 font-mono-num" /></div>
      </div>
      <div><Label className="text-xs uppercase">Category</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1" /></div>
      <DialogFooter>
        <Button onClick={() => onSubmit(form)} disabled={!form.title || loading} className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white" data-testid="submit-book">
          {loading ? "Adding..." : "Add book"}
        </Button>
      </DialogFooter>
    </div>
  );
}

function IssueForm({ book, onSuccess }) {
  const { data: students } = useQuery({ queryKey: ["students-lib"], queryFn: async () => unwrap(await api.get("/students", { params: { limit: 200 } })) });
  const [form, setForm] = useState({ student_id: "", days: 14 });
  const mut = useMutation({
    mutationFn: async () => unwrap(await api.post("/library/issues", { book_id: book.id, student_id: form.student_id, days: form.days })),
    onSuccess: () => { toast.success("Book issued"); onSuccess(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  return (
    <div className="space-y-3">
      <div className="rounded bg-[var(--bg)] p-3">
        <div className="font-semibold">{book.title}</div>
        <div className="text-xs text-[var(--text-muted)]">{book.author}</div>
      </div>
      <div>
        <Label className="text-xs uppercase">Student</Label>
        <Select value={form.student_id} onValueChange={(v) => setForm({ ...form, student_id: v })}>
          <SelectTrigger className="mt-1" data-testid="issue-student"><SelectValue placeholder="Choose student" /></SelectTrigger>
          <SelectContent className="max-h-60">
            {(students || []).map((s) => <SelectItem key={s.id} value={s.id}>{s.first_name} {s.last_name} · {s.admission_no}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div><Label className="text-xs uppercase">Days</Label><Input type="number" min="1" value={form.days} onChange={(e) => setForm({ ...form, days: Number(e.target.value) })} className="mt-1 font-mono-num" data-testid="issue-days" /></div>
      <DialogFooter>
        <Button onClick={() => mut.mutate()} disabled={!form.student_id || mut.isPending} className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white" data-testid="submit-issue">
          {mut.isPending ? "Issuing..." : "Issue book"}
        </Button>
      </DialogFooter>
    </div>
  );
}

function IssuesTab() {
  const [status, setStatus] = useState("all");
  const issueStatusesLookup = useLookupValues("library_issue_statuses");
  const lookupStatusOptions = (issueStatusesLookup.data?.values || []).map((row) => ({ value: row.value, label: row.label }));
  const statusOptions = lookupStatusOptions;
  const qc = useQueryClient();
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["issues", status],
    queryFn: async () => unwrap(await api.get("/library/issues", { params: status !== "all" ? { status } : {} })),
  });
  const returnMut = useMutation({
    mutationFn: async (id) => unwrap(await api.post(`/library/issues/${id}/return`, {})),
    onSuccess: () => { toast.success("Returned"); refetch(); qc.invalidateQueries({ queryKey: ["books"] }); },
    onError: (e) => toast.error(errMsg(e)),
  });

  const today = new Date().toISOString().slice(0, 10);
  return (
    <div className="mt-6 space-y-4">
      <div className="flex items-center gap-3">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-48" data-testid="issue-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            {statusOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Card className="border-[var(--subtle)]">
        <CardContent className="p-0">
          {isLoading ? <Skeleton className="h-64" /> : (
            <div className="overflow-x-auto"><table className="w-full text-sm grid-table">
              <thead><tr>
                <th className="text-left px-3 py-3">Book</th>
                <th className="text-left px-3 py-3">Student</th>
                <th className="text-left px-3 py-3">Issued</th>
                <th className="text-left px-3 py-3">Due</th>
                <th className="text-right px-3 py-3">Fine</th>
                <th className="text-left px-3 py-3">Status</th>
                <th className="text-left px-3 py-3">Action</th>
              </tr></thead>
              <tbody>
                {(data || []).map((iss) => {
                  const overdue = iss.status === "issued" && iss.due_date < today;
                  return (
                    <tr key={iss.id} data-testid={`issue-${iss.id}`}>
                      <td className="px-3 py-3 font-medium">{iss.book?.title}</td>
                      <td className="px-3 py-3">{iss.student ? `${iss.student.first_name} ${iss.student.last_name}` : "—"}</td>
                      <td className="px-3 py-3 font-mono-num text-xs">{formatDate(iss.issued_at)}</td>
                      <td className={`px-3 py-3 font-mono-num text-xs ${overdue ? "text-[var(--error)] font-semibold" : ""}`}>{formatDate(iss.due_date)}</td>
                      <td className="px-3 py-3 text-right font-mono-num">{iss.fine_paise ? formatMoney(iss.fine_paise) : "—"}</td>
                      <td className="px-3 py-3">
                        <Badge className={iss.status === "returned" ? "bg-[var(--success-bg)] text-[var(--success)] border-0" : (overdue ? "bg-[var(--error-bg)] text-[var(--error)] border-0" : "bg-[var(--info-bg)] text-[var(--info)] border-0")}>
                          {overdue && iss.status === "issued" ? "Overdue" : iss.status}
                        </Badge>
                      </td>
                      <td className="px-3 py-3">
                        {iss.status === "issued" && (
                          <Button size="sm" onClick={() => returnMut.mutate(iss.id)} className="h-8 bg-[var(--brand)] hover:bg-[var(--brand-hover)] text-white" data-testid={`return-${iss.id}`}>
                            Return
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {(data || []).length === 0 && !isLoading && <tr><td colSpan={7} className="py-8 text-center text-[var(--text-muted)]">No issues.</td></tr>}
              </tbody>
            </table></div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
